/**
 * Safe expression evaluator that replaces eval() for conditional logic.
 *
 * This module provides secure evaluation of comparison operations without
 * allowing arbitrary code execution, eliminating the need for 'unsafe-eval'
 * in the Content Security Policy.
 *
 * Supported comparison operators: ===, !==, ==, !=, >, <, >=, <=
 */

type ComparisonOperator = '===' | '!==' | '==' | '!=' | '>' | '<' | '>=' | '<='
type LogicalOperator = '&&' | '||'

/**
 * Safely evaluates a comparison expression without using eval().
 *
 * @param leftValue - The left operand value
 * @param operator - The comparison operator
 * @param rightValue - The right operand value
 * @returns The boolean result of the comparison
 *
 * @example
 * safeCompare(5, '>', 3) // returns true
 * safeCompare('hello', '===', 'hello') // returns true
 * safeCompare(10, '<=', 5) // returns false
 */
export function safeCompare(
  leftValue: any,
  operator: ComparisonOperator,
  rightValue: any
): boolean {
  switch (operator) {
    case '===':
      return leftValue === rightValue
    case '!==':
      return leftValue !== rightValue
    case '==':
      // eslint-disable-next-line eqeqeq
      return leftValue == rightValue
    case '!=':
      // eslint-disable-next-line eqeqeq
      return leftValue != rightValue
    case '>':
      return leftValue > rightValue
    case '<':
      return leftValue < rightValue
    case '>=':
      return leftValue >= rightValue
    case '<=':
      return leftValue <= rightValue
    default:
      throw new Error(`Unsupported comparison operator: ${operator}`)
  }
}

/**
 * Validates that a string is a safe comparison operator.
 *
 * @param operator - The operator string to validate
 * @returns True if the operator is safe to use
 */
export function isValidComparisonOperator(
  operator: string
): operator is ComparisonOperator {
  const validOperators: ComparisonOperator[] = [
    '===',
    '!==',
    '==',
    '!=',
    '>',
    '<',
    '>=',
    '<=',
  ]
  return validOperators.includes(operator as ComparisonOperator)
}

/**
 * Validates that a string is a safe logical operator.
 *
 * @param operator - The operator string to validate
 * @returns True if the operator is safe to use
 */
export function isValidLogicalOperator(
  operator: string
): operator is LogicalOperator {
  return operator === '&&' || operator === '||'
}
